import React, { useCallback, useEffect, useState } from 'react';
import {
  Alert, Box, Button, Checkbox, CircularProgress, Dialog, DialogActions, DialogContent, DialogTitle, FormControlLabel,
  Paper, Tab, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Tabs, TextField, Typography,
} from '@mui/material';
import { useAuth } from '../../context/AuthContext';
import { useToast } from '../../context/ToastContext';
import { AuthService } from '../../services/auth.service';
import { DunningService } from '../../services/collections.service';
import { PERMISSIONS } from '../../constants/permissions';
import { DunningLevel, DunningPreviewRow, DunningRun } from '../../interfaces/sales/collections.interface';
import { formatDate } from '../../utils/dateUtils';
import { useFocusedRow } from '../../hooks/useFocusedRow';
import { formatMoney, humanizeEnum } from '../../utils/format';

/** W3-N4: dunning levels, manual run (preview then send) and history. */
const DunningLetters: React.FC = () => {
  const institutionId = AuthService.getSelectedInstitution() as string;
  const { hasPermission } = useAuth();
  const toast = useToast();
  const canSend = hasPermission(PERMISSIONS.RECEIPT_MANAGE);
  const canConfigure = hasPermission(PERMISSIONS.SALES_SETTINGS_MANAGE);
  const [tab, setTab] = useState<'run' | 'levels' | 'history'>('run');
  const [levels, setLevels] = useState<DunningLevel[]>([]);
  const [preview, setPreview] = useState<DunningPreviewRow[]>([]);
  const [history, setHistory] = useState<DunningRun[]>([]);
  const [selected, setSelected] = useState<Set<string>>(new Set());
  // A notification link marks the customer's row; it is not filtered, so Send never acts on hidden rows.
  const { focusId: focusCustomerId } = useFocusedRow('customerId');
  const [editing, setEditing] = useState<DunningLevel | null>(null);
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);
  const [confirmSend, setConfirmSend] = useState(false);

  const load = useCallback(async () => {
    try {
      setLoading(true);
      const [l, p, h] = await Promise.all([
        DunningService.levels(institutionId), DunningService.preview(institutionId), DunningService.history(institutionId),
      ]);
      setLevels(l); setPreview(p); setHistory(h);
      setSelected(new Set(p.filter(r => r.willSend).map(r => r.customerId)));
    } catch (e) {
      toast.showApiError(e, 'Failed to load dunning');
    } finally {
      setLoading(false);
    }
  }, [institutionId]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => { load(); }, [load]);

  const loadDefaults = async () => {
    try {
      const r = await DunningService.loadDefaults(institutionId);
      toast.showSuccess(`${r.created} level(s) added, ${r.skipped} already present`);
      await load();
    } catch (e) {
      toast.showApiError(e, 'Failed to load default levels');
    }
  };
  const send = async () => {
    try {
      setSending(true);
      const run = await DunningService.send(institutionId, Array.from(selected));
      toast.showSuccess(`${run.lettersSent} letter(s) sent, ${run.lettersSkipped} skipped`);
      await load();
    } catch (e) {
      toast.showApiError(e, 'Failed to send dunning letters');
    } finally {
      setSending(false);
    }
  };
  const saveLevel = async () => {
    if (!editing) return;
    try {
      await DunningService.saveLevel(editing);
      setEditing(null);
      await load();
    } catch (e) {
      toast.showApiError(e, 'Failed to save the level');
    }
  };
  const removeLevel = async (id: string) => {
    try { await DunningService.deleteLevel(id); await load(); } catch (e) { toast.showApiError(e, 'Failed to delete the level'); }
  };

  return (
    <Box sx={{ p: 3 }} data-help-page="sales.dunning.page">
      <Typography variant="h5" gutterBottom>Dunning</Typography>
      <Tabs value={tab} onChange={(_, v) => setTab(v)} sx={{ mb: 2 }}>
        <Tab data-help="sales.dunning.tab.run" value="run" label="Run" /><Tab data-help="sales.dunning.tab.levels" value="levels" label="Levels" /><Tab data-help="sales.dunning.tab.history" value="history" label="History" />
      </Tabs>
      {loading ? <CircularProgress /> : (
        <>
          {tab === 'run' && (
            <>
              {levels.length === 0 && <Alert severity="info" sx={{ mb: 2 }}>No dunning levels yet. Open the Levels tab and load the defaults.</Alert>}
              <TableContainer component={Paper} data-help-section="sales.dunning.runTable">
                <Table size="small">
                  <TableHead><TableRow>
                    <TableCell data-help="sales.dunning.run.col.pick" /><TableCell data-help="sales.dunning.run.col.customer">Customer</TableCell><TableCell data-help="sales.dunning.run.col.level">Level</TableCell>
                    <TableCell align="right" data-help="sales.dunning.run.col.total">Overdue total</TableCell><TableCell data-help="sales.dunning.run.col.invoices">Invoices</TableCell><TableCell data-help="sales.dunning.run.col.note">Note</TableCell>
                  </TableRow></TableHead>
                  <TableBody>
                    {preview.length === 0 && <TableRow><TableCell colSpan={6} align="center">No customer has reached a new level.</TableCell></TableRow>}
                    {preview.map(r => (
                      <TableRow key={r.customerId} selected={r.customerId === focusCustomerId}>
                        <TableCell padding="checkbox">
                          <Checkbox data-help="sales.dunning.run.pick" disabled={!r.willSend} checked={selected.has(r.customerId)}
                            onChange={e => {
                              const n = new Set(selected);
                              if (e.target.checked) n.add(r.customerId); else n.delete(r.customerId);
                              setSelected(n);
                            }} />
                        </TableCell>
                        <TableCell>{r.customerName}</TableCell>
                        <TableCell>{r.levelNumber} - {r.levelName}</TableCell>
                        <TableCell align="right">{formatMoney(r.total)}</TableCell>
                        <TableCell>{r.invoices.map(i => i.invoiceNumber).join(', ')}</TableCell>
                        <TableCell>{r.willSend ? r.email : r.skipReason}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
              {canSend && <Button data-help="sales.dunning.run.send" sx={{ mt: 2 }} variant="contained" disabled={sending || selected.size === 0} onClick={() => setConfirmSend(true)}>
                Send {selected.size} letter(s)</Button>}
            </>
          )}
          {tab === 'levels' && (
            <>
              {canConfigure && (
                <Box sx={{ display: 'flex', gap: 1, mb: 2 }}>
                  <Button data-help="sales.dunning.levels.loadDefaults" variant="outlined" onClick={loadDefaults}>Load default levels</Button>
                  <Button data-help="sales.dunning.levels.add" variant="outlined" onClick={() => setEditing({
                    institutionId, levelNumber: levels.length + 1, name: '', minDaysOverdue: 7, subjectTemplate: '',
                    bodyTemplate: 'Dear {customer},\n\nThe following invoices are overdue:\n{invoices}\n\nTotal: {total}', attachStatement: true, enabled: true,
                  })}>Add level</Button>
                </Box>
              )}
              <TableContainer component={Paper} data-help-section="sales.dunning.levelsTable">
                <Table size="small">
                  <TableHead><TableRow><TableCell data-help="sales.dunning.levels.col.number">#</TableCell><TableCell data-help="sales.dunning.levels.col.name">Name</TableCell><TableCell data-help="sales.dunning.levels.col.days">Days overdue</TableCell><TableCell data-help="sales.dunning.levels.col.enabled">Enabled</TableCell><TableCell data-help="sales.dunning.levels.col.actions" /></TableRow></TableHead>
                  <TableBody>
                    {levels.map(l => (
                      <TableRow key={l.id}>
                        <TableCell>{l.levelNumber}</TableCell><TableCell>{l.name}</TableCell>
                        <TableCell>{l.minDaysOverdue}</TableCell><TableCell>{l.enabled ? 'Yes' : 'No'}</TableCell>
                        <TableCell align="right">
                          {canConfigure && <><Button data-help="sales.dunning.levels.edit" size="small" onClick={() => setEditing(l)}>Edit</Button>
                            <Button data-help="sales.dunning.levels.delete" size="small" color="error" onClick={() => removeLevel(l.id as string)}>Delete</Button></>}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </>
          )}
          {tab === 'history' && (
            <TableContainer component={Paper} data-help-section="sales.dunning.historyTable">
              <Table size="small">
                <TableHead><TableRow><TableCell data-help="sales.dunning.history.col.date">Run date</TableCell><TableCell data-help="sales.dunning.history.col.sent">Sent</TableCell><TableCell data-help="sales.dunning.history.col.skipped">Skipped</TableCell><TableCell data-help="sales.dunning.history.col.letters">Letters</TableCell></TableRow></TableHead>
                <TableBody>
                  {history.map(h => (
                    <TableRow key={h.id}>
                      <TableCell>{formatDate(h.runDate)}</TableCell><TableCell>{h.lettersSent}</TableCell><TableCell>{h.lettersSkipped}</TableCell>
                      <TableCell>{(h.letters ?? []).map(l => `${l.customerName} L${l.levelNumber} ${humanizeEnum(l.status)}`).join('; ')}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </>
      )}
      {confirmSend && (
        <Dialog open onClose={() => setConfirmSend(false)} maxWidth="sm" fullWidth data-help-page="sales.dunning.confirm.page">
          <DialogTitle>Send {selected.size} dunning letter(s)?</DialogTitle>
          <DialogContent data-help-section="sales.dunning.confirm.section.list">
            {preview.filter(r => selected.has(r.customerId)).map(r => (
              <Typography key={r.customerId} variant="body2">
                {r.customerName} ({r.email ?? 'no e-mail'}) - level {r.levelNumber}, {formatMoney(r.total)}
              </Typography>
            ))}
          </DialogContent>
          <DialogActions>
            <Button data-help="sales.dunning.confirm.cancel" onClick={() => setConfirmSend(false)}>Cancel</Button>
            <Button data-help="sales.dunning.confirm.send" variant="contained" disabled={sending} onClick={async () => { setConfirmSend(false); await send(); }}>Send</Button>
          </DialogActions>
        </Dialog>
      )}
      {editing && (
        <Dialog open onClose={() => setEditing(null)} maxWidth="sm" fullWidth data-help-page="sales.dunning.levelDialog.page">
          <DialogTitle>{editing.id ? 'Edit level' : 'Add level'}</DialogTitle>
          <DialogContent data-help-section="sales.dunning.levelDialog.section.form" sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: '8px !important' }}>
            <TextField data-help="sales.dunning.levelDialog.number" label="Level number" type="number" value={editing.levelNumber} fullWidth
              onChange={e => setEditing({ ...editing, levelNumber: Number(e.target.value) })} />
            <TextField data-help="sales.dunning.levelDialog.name" label="Name" value={editing.name} fullWidth onChange={e => setEditing({ ...editing, name: e.target.value })} />
            <TextField data-help="sales.dunning.levelDialog.days" label="Minimum days overdue" type="number" value={editing.minDaysOverdue} fullWidth
              onChange={e => setEditing({ ...editing, minDaysOverdue: Number(e.target.value) })} />
            <TextField data-help="sales.dunning.levelDialog.subject" label="Subject" value={editing.subjectTemplate} fullWidth onChange={e => setEditing({ ...editing, subjectTemplate: e.target.value })} />
            <TextField data-help="sales.dunning.levelDialog.body" label="Body ({customer}, {invoices}, {total}, {payLinks})" value={editing.bodyTemplate} multiline minRows={4} fullWidth
              onChange={e => setEditing({ ...editing, bodyTemplate: e.target.value })} />
            <FormControlLabel data-help="sales.dunning.levelDialog.attach" control={<Checkbox checked={editing.attachStatement} onChange={e => setEditing({ ...editing, attachStatement: e.target.checked })} />} label="Attach statement PDF" />
            <FormControlLabel data-help="sales.dunning.levelDialog.enabled" control={<Checkbox checked={editing.enabled} onChange={e => setEditing({ ...editing, enabled: e.target.checked })} />} label="Enabled" />
          </DialogContent>
          <DialogActions><Button data-help="sales.dunning.levelDialog.cancel" onClick={() => setEditing(null)}>Cancel</Button><Button data-help="sales.dunning.levels.save" variant="contained" onClick={saveLevel}>Save</Button></DialogActions>
        </Dialog>
      )}
    </Box>
  );
};

export default DunningLetters;
